import { ReactElement, useMemo } from 'react';
import {
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  AbsoluteFill,
} from 'remotion';

import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';
import { combineClasses } from '@/utils';

// Fonts
import { lalezarFontFamily } from '@/styles/fonts';

// Types
import { AnimationPreset } from '@/types';
import { Project } from './types';

// Constants
import {
  ANIMATION_SPRING_CONFIGS,
  CLAMP_BOTH,
  SCENE_3B_TEXT_FADE_OUT_DURATION,
} from '@/constants';
import { ANIMATION_CONFIG } from '../constants';

// Utils
import { getRecentProjects } from '../utils';

interface ProjectItemProps {
  project: Project;
  slideProgress: number;
  opacity: number;
}

const {
  animationStartFrame,
  batchDisplayDuration,
  projectsPerBatch,
  slideDistance,
  staggerDelay,
  fadeDuration,
} = ANIMATION_CONFIG.projectListComponent;

const ProjectItem = ({
  project,
  slideProgress,
  opacity,
}: ProjectItemProps): ReactElement => {
  const { colors, typography } = employeeYearInReviewTheme;

  const translateY = interpolate(
    slideProgress,
    [0, 1],
    [slideDistance, 0],
    CLAMP_BOTH,
  );

  // Format date range (e.g., "Jan - Mar 2025")
  const formatDateRange = (joinedAt?: string, leftAt?: string): string => {
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    if (!joinedAt || !leftAt) return '';

    const start = new Date(joinedAt);
    const end = new Date(leftAt);
    const startMonth = monthNames[start.getMonth()];
    const endMonth = monthNames[end.getMonth()];
    const year = end.getFullYear();

    return `${startMonth} - ${endMonth} ${year}`;
  };

  return (
    <div
      className={combineClasses(
        'w-[650px] flex flex-col gap-y-4',
        typography.xs,
        colors.textPrimary,
      )}
      style={{
        transform: `translateY(${translateY}px)`,
        opacity,
      }}
    >
      <p>{project.code}</p>
      <p
        className={colors.textPrimary}
        style={{
          fontFamily: lalezarFontFamily,
        }}
      >
        {formatDateRange(project.joinedAt, project.leftAt)}
      </p>
      <p
        className={typography.md}
        style={{
          fontFamily: lalezarFontFamily,
        }}
      >
        {project.role}
      </p>
    </div>
  );
};

export interface ProjectListProps {
  projects: Project[];
  /** Frame when text should start fading out (relative to this component's frame) */
  textFadeOutStartFrame?: number;
}

export const ProjectList = ({
  projects,
  textFadeOutStartFrame,
}: ProjectListProps): ReactElement => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Get the most recent projects (sorted by joinedAt, max 5)
  const displayProjects = useMemo(
    () => getRecentProjects(projects),
    [projects],
  );

  // Text fade out animation before transition
  // If textFadeOutStartFrame is provided, fade out text from 1 to 0 over SCENE_3B_TEXT_FADE_OUT_DURATION
  const contentFadeOutOpacity = textFadeOutStartFrame
    ? interpolate(
        frame,
        [
          textFadeOutStartFrame,
          textFadeOutStartFrame + SCENE_3B_TEXT_FADE_OUT_DURATION,
        ],
        [1, 0],
        CLAMP_BOTH,
      )
    : 1;

  // Calculate which batch is currently active
  const totalBatches = Math.ceil(displayProjects.length / projectsPerBatch);

  // Get batches of projects
  const batches = useMemo(() => {
    const result: Project[][] = [];
    for (let i = 0; i < displayProjects.length; i += projectsPerBatch) {
      result.push(displayProjects.slice(i, i + projectsPerBatch));
    }
    return result;
  }, [displayProjects]);

  // Adjusted frame relative to animation start
  const adjustedFrame = Math.max(0, frame - animationStartFrame);

  // Current batch index based on adjusted frame
  const currentBatchIndex = Math.min(
    Math.floor(adjustedFrame / batchDisplayDuration),
    totalBatches - 1,
  );

  // Calculate animation progress for each batch
  const getBatchAnimation = (
    batchIndex: number,
    itemIndexInBatch: number,
  ): {
    slideProgress: number;
    opacity: number;
    exitProgress: number;
  } => {
    const batchStartFrame =
      animationStartFrame + batchIndex * batchDisplayDuration;
    const itemStartFrame = batchStartFrame + itemIndexInBatch * staggerDelay;
    const localFrame = frame - itemStartFrame;

    // Slide in with smooth gentle motion
    const slideProgress = spring({
      frame: localFrame,
      fps,
      config: ANIMATION_SPRING_CONFIGS[AnimationPreset.GENTLE],
      durationInFrames: 30, // Smoother, longer slide animation
    });

    // Fade in - longer duration for smoother appearance
    const opacity = interpolate(
      frame,
      [itemStartFrame, itemStartFrame + fadeDuration],
      [0, 1],
      CLAMP_BOTH,
    );

    // Exit animation - starts sliding up when next batch appears
    const exitStartFrame =
      animationStartFrame + (batchIndex + 1) * batchDisplayDuration;
    const exitProgress = spring({
      frame: frame - exitStartFrame,
      fps,
      config: ANIMATION_SPRING_CONFIGS[AnimationPreset.GENTLE],
      durationInFrames: 25, // Smoother exit animation
    });

    return { slideProgress, opacity, exitProgress };
  };

  return (
    <AbsoluteFill
      className="flex-center"
      style={{ opacity: contentFadeOutOpacity }}
    >
      <div className="relative w-full h-full flex-center overflow-hidden">
        {batches.map((batch, batchIndex) => {
          // Only render current and previous batch for smooth transition
          const isCurrentBatch = batchIndex === currentBatchIndex;
          const isPreviousBatch = batchIndex === currentBatchIndex - 1;
          const isFutureBatch = batchIndex > currentBatchIndex;
          const isOldBatch = batchIndex < currentBatchIndex - 1;

          // Don't render future batches or old batches (only current and previous)
          if (isFutureBatch || isOldBatch) return null;

          return (
            <div
              key={batchIndex}
              className="absolute flex flex-col gap-y-20"
              style={{
                // Move previous batch up and out
                transform: isPreviousBatch
                  ? `translateY(${interpolate(
                      getBatchAnimation(batchIndex, 0).exitProgress,
                      [0, 1],
                      [0, -slideDistance],
                      CLAMP_BOTH,
                    )}px)`
                  : undefined,
                opacity: isPreviousBatch
                  ? interpolate(
                      getBatchAnimation(batchIndex, 0).exitProgress,
                      [0, 0.5],
                      [1, 0],
                      CLAMP_BOTH,
                    )
                  : 1,
              }}
            >
              {batch.map((project, itemIndex) => {
                const { slideProgress, opacity } = getBatchAnimation(
                  batchIndex,
                  itemIndex,
                );

                return (
                  <ProjectItem
                    key={`${batchIndex}-${itemIndex}`}
                    project={project}
                    slideProgress={isCurrentBatch ? slideProgress : 1}
                    opacity={isCurrentBatch ? opacity : 1}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
